import React, { useEffect, useState } from 'react';
import { MenuProduct } from '../services/MenuService';
import { useCart } from '../contexts/CartContext.tsx';
import { useProducts } from '../contexts/ProductsContext.tsx';
import YouMightLike from './YouMightLike.tsx';
import { wouldExceedInfusedLimit, INFUSED_LIMIT_GRAMS } from '../utils/marylandInfusedLimit.ts';
import './ProductDetailDrawer.css';

// Helper functions for weight display (same as DutchieMenu)
const sanitizeCategoryName = (category: string): string => {
  return category?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
};

const shouldDisplayWeight = (product: MenuProduct): boolean => {
  if (!product || !product.category) return false;
  
  // Don't show weight for edibles, accessories, or topicals
  const sanitizedCategory = sanitizeCategoryName(product.category).toLowerCase();
  const isEdible = sanitizedCategory === 'edibles' || sanitizedCategory === 'edible';
  const isAccessory = sanitizedCategory === 'accessories' || sanitizedCategory === 'accessory';
  const isTopical = sanitizedCategory === 'topicals' || sanitizedCategory === 'topical';

  return !isEdible && !isAccessory && !isTopical;
};

const formatWeight = (weight: string): string => {
  if (!weight || weight === 'all') return weight;
  
  // If the weight starts with a decimal point, add leading zero
  if (weight.startsWith('.')) {
    return '0' + weight;
  }
  
  return weight;
};

const getWeightDisplayText = (product: MenuProduct, menuId?: string): string => {
  if (!shouldDisplayWeight(product) || !product.variants || product.variants.length === 0) {
    return '';
  }
  
  // Only apply Vermont-specific logic for Essex Junction store
  const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;
  
  if (isVermontStore) {
    // Show weight info for FLOWER and CONCENTRATES categories in Vermont
    const category = product.category?.toLowerCase();
    if (category === 'flower' || category === 'concentrates') {
      if (product.variants.length === 1) {
        // Single variant: show the variant option
        const variant = product.variants[0];
        return formatWeight(variant.option).toUpperCase();
      } else {
        // Multiple variants: show range format (e.g., "1G - 3G")
        const sortedVariants = getSortedVariants();
        
        if (sortedVariants.length <= 1) {
          return sortedVariants.map(v => formatWeight(v.option).toUpperCase()).join('');
        }
        
        const firstVariant = formatWeight(sortedVariants[0].option).toUpperCase();
        const lastVariant = formatWeight(sortedVariants[sortedVariants.length - 1].option).toUpperCase();
        
        return `${firstVariant} - ${lastVariant}`;
      }
    }
    return '';
  }
  
  // For non-Vermont stores, just show the individual variant weight
  const variant = product.variants[0];
  return variant ? formatWeight(variant.option) : '';
};

interface AccordionSectionProps {
  title: string;
  isOpen: boolean;
  content: React.ReactNode;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({ title, isOpen: initialIsOpen, content }) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);

  return (
    <div className="accordion-section">
      <div 
        className="accordion-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="accordion-title">{title}</span>
        <svg 
          className={`accordion-chevron ${isOpen ? '' : 'collapsed'}`}
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none"
        >
          <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className={`accordion-content ${isOpen ? '' : 'collapsed'}`}>
        {content}
      </div>
    </div>
  );
};

interface ProductDetailDrawerProps {
  product: MenuProduct | null;
  isOpen: boolean;
  onClose: () => void;
  menuId?: string;
  allProducts?: MenuProduct[];
  pricingType?: 'MEDICAL' | 'RECREATIONAL';
  cartProp?: any;
  cartLoading?: boolean;
  addingToCart?: Set<string>;
  activeMarquees?: Set<string>;
  stoppingMarquees?: Set<string>;
  dealDescriptions?: Record<string, { title: string; description: string }>;
  productTileCursorStyle?: React.CSSProperties;
  isMobileProp?: boolean;
  selectedTerpenes?: string[];
  selectedCannabinoids?: string[];
  onProductClick?: (product: MenuProduct) => void;
  onProductHover?: (productId: string) => void;
  onProductLeave?: (productId: string) => void;
  onAddToCart?: (product: MenuProduct, variant: any) => void;
  onDebugClick?: (product: MenuProduct) => void;
  onDealClick?: (product: MenuProduct) => void;
  onVermontIncrement?: (product: MenuProduct, variant: any) => void;
  onVermontDecrement?: (product: MenuProduct, variant: any) => void;
  onShowError?: (message: string) => void;
}

const ProductDetailDrawer: React.FC<ProductDetailDrawerProps> = ({
  product,
  isOpen,
  onClose,
  menuId,
  allProducts = [],
  pricingType = 'RECREATIONAL',
  cartProp,
  cartLoading = false,
  addingToCart = new Set(),
  activeMarquees = new Set(),
  stoppingMarquees = new Set(),
  dealDescriptions,
  productTileCursorStyle,
  isMobileProp,
  selectedTerpenes = [],
  selectedCannabinoids = [],
  onProductClick,
  onProductHover = () => {},
  onProductLeave = () => {},
  onAddToCart,
  onDebugClick = () => {},
  onDealClick = () => {},
  onVermontIncrement,
  onVermontDecrement,
  onShowError
}) => {
  // Determine which selector to show based on store type and product category
  const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;
  
  // Helper function to detect Connecticut stores
  const isConnecticutStore = (storeId?: string): boolean => {
    const connecticutStoreIds = [
      '2ca5693a-0ce8-421a-8e6b-f07c2b498a29', // West Hartford
      '9ac9d5ad-5e4e-41a3-b37f-6da013b423ae'  // Stamford
    ];
    return storeId ? connecticutStoreIds.includes(storeId) : false;
  };

  // Helper function to detect Vermont stores (similar to Connecticut helper)
  const isVermontStoreById = (storeId?: string): boolean => {
    const vermontStoreIds = [
      'dcaee184-ca72-4e9f-a5fa-9c24613a0882', // Essex Junction
    ];
    return storeId ? vermontStoreIds.includes(storeId) : false;
  };
  const isFlowerProduct = product?.category?.toLowerCase() === 'flower';
  const isConcentrateProduct = product?.category?.toLowerCase() === 'concentrates';
  const hasMultipleVariants = product?.variants && product.variants.length > 1;
  const showWeightSelector = isVermontStore && (isFlowerProduct || (isConcentrateProduct && hasMultipleVariants));
  const showQuantitySelector = !showWeightSelector;
  const [show, setShow] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [selectedWeight, setSelectedWeight] = useState(1); // Weight in grams
  const [selectedVariant, setSelectedVariant] = useState<any>(null); // For Vermont variant cycling
  const [variantDropdownOpen, setVariantDropdownOpen] = useState(false); // For custom dropdown
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Track selected image
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { addToCart, updateQuantity, removeFromCart, cart } = useCart();

  // Track previous isOpen state to detect actual open/close transitions
  const wasOpenRef = React.useRef(false);
  const { products: menuProducts } = useProducts();

  // Helper functions for Vermont variant cycling
  const getSortedVariants = React.useCallback(() => {
    if (!product.variants || product.variants.length === 0) {
      return [];
    }
    
    return [...product.variants].sort((a, b) => {
      const weightA = a.flowerEquivalent?.value || parseWeightFromOption(a.option) || 0;
      const weightB = b.flowerEquivalent?.value || parseWeightFromOption(b.option) || 0;
      return weightA - weightB;
    });
  }, [product]);

  const getCurrentVariantFromCart = React.useCallback(() => {
    if (!cart || !cart.items || !product) return null;
    
    const cartItem = cart.items.find(item => item.productId === product.id);
    if (!cartItem || !cartItem.option) return null;
    
    return product.variants?.find(v => v.option === cartItem.option) || null;
  }, [cart, product]);


  // Helper function to find existing cart item for the current product (legacy)
  const findExistingCartItem = React.useCallback(() => {
    if (!cart || !cart.items || !product) {
      return null;
    }

    // For Vermont, find any cart item for this product
    if (isVermontStore) {
      return cart.items.find(item => item.productId === product.id) || null;
    }

    // For non-Vermont, find the 1g variant to match against cart items
    const oneGramVariant = product.variants?.find(variant => 
      variant.option === '1g' || variant.option === '1.0g'
    ) || product.variants?.[0];

    if (!oneGramVariant) {
      return null;
    }

    // Look for cart item with matching product ID and variant option
    return cart.items.find(item => 
      item.productId === product.id && item.option === oneGramVariant.option
    ) || null;
  }, [cart, product, isVermontStore]);

  // Helper function to get current cart quantity for this product
  const getCartQuantityForProduct = React.useCallback(() => {
    const existingCartItem = findExistingCartItem();
    return existingCartItem ? existingCartItem.quantity : 0;
  }, [findExistingCartItem]);

  // Helper function to convert common cannabis measurements to grams
  const parseWeightFromOption = React.useCallback((option: string): number | null => {
    if (!option) return null;
    
    const cleanOption = option.toLowerCase().trim();
    
    // Direct gram parsing (e.g., "1g", "3.5g", "1", "3.5")
    const gramMatch = cleanOption.match(/^(\d+(?:\.\d+)?)g?$/);
    if (gramMatch) {
      return parseFloat(gramMatch[1]);
    }
    
    // Fractional oz parsing (e.g., "1/8oz", "1/8", "1/4oz", "1/2oz", "1oz")
    const ozMatch = cleanOption.match(/^(\d+)\/(\d+)(?:oz)?$/) || cleanOption.match(/^(\d+)(?:oz)?$/);
    if (ozMatch) {
      if (ozMatch[2]) {
        // Fraction like "1/8"
        const numerator = parseFloat(ozMatch[1]);
        const denominator = parseFloat(ozMatch[2]);
        return (numerator / denominator) * 28; // 1oz = 28g
      } else {
        // Whole number like "1oz"
        return parseFloat(ozMatch[1]) * 28;
      }
    }
    
    return null;
  }, []);

  // Helper function to get valid weight increments from product variants
  const getValidWeightIncrements = React.useCallback(() => {
    if (!product || !product.variants) {
      return [1]; // Default fallback
    }

    // Extract weights from flowerEquivalent values, or parse from option strings
    const variantWeights = product.variants.map(variant => {
      if (variant.flowerEquivalent && variant.flowerEquivalent.value) {
        return variant.flowerEquivalent.value;
      }
      // Fallback: try to parse weight from option string using enhanced parsing
      return parseWeightFromOption(variant.option);
    }).filter((weight): weight is number => weight !== null);

    // Find the maximum variant weight to determine range
    const maxWeight = Math.max(...variantWeights);
    
    // Create comprehensive weight list: all integers 1-maxWeight + any fractional variants
    const allWeights = new Set<number>();
    
    // Add all integer weights from 1 to maxWeight
    for (let i = 1; i <= maxWeight; i++) {
      allWeights.add(i);
    }
    
    // Add any fractional variant weights (like 3.5g)
    variantWeights.forEach(weight => {
      if (weight % 1 !== 0) { // Only fractional weights
        allWeights.add(weight);
      }
    });

    return Array.from(allWeights).sort((a, b) => a - b);
  }, [product, parseWeightFromOption]);

  // Helper functions for smart increment/decrement
  const getNextWeight = React.useCallback((currentWeight: number) => {
    const validWeights = getValidWeightIncrements();
    const currentIndex = validWeights.indexOf(currentWeight);
    
    if (currentIndex >= 0 && currentIndex < validWeights.length - 1) {
      // Move to next valid weight
      return validWeights[currentIndex + 1];
    } else if (currentIndex === -1) {
      // Current weight not in valid list, find next higher weight
      const nextWeight = validWeights.find(w => w > currentWeight);
      return nextWeight || Math.max(...validWeights);
    }
    
    // Already at max, keep incrementing by 1g
    return currentWeight + 1;
  }, [getValidWeightIncrements]);

  const getPreviousWeight = React.useCallback((currentWeight: number) => {
    const validWeights = getValidWeightIncrements();
    const currentIndex = validWeights.indexOf(currentWeight);
    
    if (currentIndex > 0) {
      // Move to previous valid weight
      return validWeights[currentIndex - 1];
    } else if (currentIndex === -1) {
      // Current weight not in valid list, find next lower weight
      const prevWeight = validWeights.slice().reverse().find(w => w < currentWeight);
      return prevWeight || Math.min(...validWeights);
    }
    
    // Already at minimum valid weight
    return Math.max(1, validWeights[0]);
  }, [getValidWeightIncrements]);

  // Simplified variant matching - only handle 3.5g specially
  const getVariantForWeight = React.useCallback((targetWeight: number): any => {
    if (!product || !product.variants) {
      return product?.variants?.[0] || null;
    }

    // Special case: 3.5g uses 1/8oz variant
    if (targetWeight === 3.5) {
      const eighthOzVariant = product.variants.find(variant => 
        (variant.flowerEquivalent && variant.flowerEquivalent.value === 3.5) ||
        variant.option === '1/8oz' || 
        variant.option === '1/8'
      );
      
      if (eighthOzVariant) {
        return eighthOzVariant;
      }
    }

    // All other weights: use 1g variant
    const oneGramVariant = product.variants.find(variant => 
      variant.option === '1g' || variant.option === '1.0g'
    );

    return oneGramVariant || product.variants[0];
  }, [product]);

  const calculateQuantityForVariant = React.useCallback((targetWeight: number, variant: any): number => {
    // Special case: 3.5g with 1/8oz variant = quantity 1
    if (targetWeight === 3.5) {
      return 1;
    }

    // All other weights: use integer quantity with 1g variant
    return Math.max(1, Math.round(targetWeight));
  }, []);

  // Define preventScroll outside useEffect so we can properly remove listeners
  const preventScroll = React.useCallback((e: Event) => {
    const target = e.target as Element;
    
    // Allow scrolling within the drawer and its children
    if (target && target.closest('.product-detail-drawer')) {
      return;
    }
    
    // Prevent scrolling everywhere else
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  useEffect(() => {
    // Initialize quantity, weight, variant, and image when opening drawer
    if (isOpen) {
      // Always reset image to first
      setSelectedImageIndex(0);
      
      // Check if product is already in cart
      const existingCartItem = findExistingCartItem();
      
      if (existingCartItem) {
        // Product exists in cart - populate with current cart values
        if (isVermontStore && (isFlowerProduct || (isConcentrateProduct && hasMultipleVariants))) {
          // Vermont flower or multi-variant concentrates: Set selected variant to match cart item
          const currentVariant = getCurrentVariantFromCart();
          setSelectedVariant(currentVariant || getSortedVariants()[0]);
          setSelectedQuantity(1); // Always quantity 1 for variant selector products
          setSelectedWeight(1); // Legacy - not used for variant cycling
        } else {
          // Other stores OR Vermont single-variant products: quantity from cart goes to quantity selector
          setSelectedQuantity(existingCartItem.quantity);
          setSelectedWeight(1);
        }
      } else {
        // Product not in cart - use defaults
        if (isVermontStore && (isFlowerProduct || (isConcentrateProduct && hasMultipleVariants))) {
          // Vermont flower or multi-variant concentrates: Set to first (smallest) variant
          setSelectedVariant(getSortedVariants()[0] || null);
          setSelectedQuantity(1);
        } else {
          // Other stores OR Vermont single-variant products: regular defaults
          setSelectedQuantity(1);
          setSelectedWeight(1);
        }
      }
    }

    if (isOpen) {
      // Mark that drawer is now open (for detecting actual close transitions)
      wasOpenRef.current = true;

      // Save current scroll position
      const scrollY = window.scrollY;

      // Apply position: fixed with top offset to prevent scroll jump
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      // Set CSS variable so sticky bars can compensate for the offset
      document.body.style.setProperty('--scroll-offset', `${scrollY}px`);
      document.body.classList.add('drawer-open');

      // Block wheel events on window and document, but allow within drawer
      window.addEventListener('wheel', preventScroll, { passive: false });
      window.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('touchmove', preventScroll, { passive: false });

      // Show the drawer first
      setShow(true);
      // Wait longer to ensure DOM is rendered and CSS applied
      const timer = setTimeout(() => setAnimate(true), 100);
      return () => clearTimeout(timer);
    } else {
      // Only run close logic if we're actually transitioning from open to closed
      // This prevents scroll jumps when useEffect re-runs due to cart-dependent callbacks changing
      if (wasOpenRef.current) {
        // Get the scroll position from the body's top value
        const scrollY = document.body.style.top;

        // Remove fixed positioning and class
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.removeProperty('--scroll-offset');
        document.body.classList.remove('drawer-open');

        // Restore scroll position instantly (no smooth scrolling)
        window.scrollTo(0, parseInt(scrollY || '0') * -1);

        // Force style recalculation to ensure clean state
        void document.body.offsetHeight;

        // Remove scroll event listeners
        window.removeEventListener('wheel', preventScroll);
        window.removeEventListener('touchmove', preventScroll);
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventScroll);

        // Start closing animation immediately
        setAnimate(false);
        // Hide after animation completes
        const timer = setTimeout(() => setShow(false), 450);
        wasOpenRef.current = false;
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, product, preventScroll, findExistingCartItem, getCurrentVariantFromCart, getSortedVariants, isVermontStore]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (variantDropdownOpen && !target.closest('.drawer-variant-dropdown-container')) {
        setVariantDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [variantDropdownOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Cleanup effect to ensure scrolling is restored if component unmounts while drawer is open
  useEffect(() => {
    return () => {
      // Get the scroll position from the body's top value before cleanup
      const scrollY = document.body.style.top;

      // Remove all body styles and class
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.removeProperty('--scroll-offset');
      document.body.classList.remove('drawer-open');

      // Restore scroll position if it was set
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }

      // Force style recalculation on cleanup
      void document.body.offsetHeight;

      // Clean up any remaining event listeners with the same function reference
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [preventScroll]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!product || !show) {
    return null;
  }

  const formatStrainType = (strainType: string): string => {
    if (!strainType) return '';
    if (strainType === 'NOT_APPLICABLE') return '';
    if (strainType === 'ONE_TO_ONE') return '1:1';
    if (strainType === 'SATIVA_HYBRID') return 'Sativa-Hybrid';
    if (strainType === 'INDICA_HYBRID') return 'Indica-Hybrid';
    return strainType.toLowerCase().replace(/^\w/, c => c.toUpperCase());
  };

  // Helper function to extract weights from product variants
  const getProductWeights = (product: MenuProduct): string => {
    // Only show weights for specific categories
    const allowedCategories = ['FLOWER', 'CONCENTRATES', 'PRE_ROLLS', 'Flower', 'Concentrate', 'Pre-Rolls', 'Infused Pre-Rolls'];
    if (!product.category || !allowedCategories.includes(product.category)) {
      return '';
    }

    if (!product.variants || product.variants.length === 0) {
      return '';
    }

    // Extract unique weights from variants using only the option field
    const weights = product.variants
      .map(variant => {
        // Parse from option string only - never use flowerEquivalent
        const parsed = parseWeightFromOption(variant.option);
        if (parsed) {
          return parsed === Math.floor(parsed) ? `${parsed}G` : `${parsed.toFixed(1)}G`;
        }
        
        // Return option as-is if we can't parse it
        return variant.option.toUpperCase();
      })
      .filter((weight, index, array) => array.indexOf(weight) === index) // Remove duplicates
      .sort((a, b) => {
        // Sort by numeric value if possible
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });

    // Format the weights
    if (weights.length === 1) {
      return weights[0];
    } else if (weights.length <= 4) {
      return weights.join(', ');
    } else {
      // For many weights, show range
      return `${weights[0]} - ${weights[weights.length - 1]}`;
    }
  };

  const formatProductName = (name: string): string => {
    // Split on "|" and return only the part before it
    return name.split('|')[0].trim();
  };

  const getAllCannabinoids = (product: MenuProduct) => {
    if (!product.cannabinoids || product.cannabinoids.length === 0) return [];

    return product.cannabinoids
      .filter(cannabinoid => cannabinoid.value && cannabinoid.value > 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
  };

  const formatCannabinoidValue = (value: number, unit?: string | null): string => {
    const formatted = value.toFixed(2);
    switch (unit) {
      case 'MILLIGRAMS': return `${formatted}mg`;
      case 'MILLIGRAMS_PER_GRAM': return `${formatted}mg/g`;
      case 'PERCENTAGE':
      default: return `${formatted}%`;
    }
  };

  const getAllTerpenes = (product: MenuProduct) => {
    if (!product.terpenes || product.terpenes.length === 0) return [];
    
    return product.terpenes
      .filter(terpene => terpene.value && terpene.value > 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
  };

  const getAllEffects = (product: MenuProduct) => {
    if (!product.effects || product.effects.length === 0) return [];
    
    // Format effects: "BE LIKE THIS" -> "Be Like This"
    return product.effects.map(effect => 
      effect.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  // Pricing logic with quantity multiplier and weight selection
  const getProductPricing = (product: MenuProduct, quantity: number = 1, weightInGrams: number = 1) => {
    if (!product.variants || product.variants.length === 0) {
      return { regularPrice: 0, specialPrice: null, hasDiscount: false, discountPercent: 0 };
    }

    // Find the 1g variant to use as base price
    const oneGramVariant = product.variants.find(variant =>
      variant.option === '1g' || variant.option === '1.0g'
    ) || product.variants[0]; // Fallback to first variant if no 1g found

    // Use the pricingType prop passed from parent component
    const baseRegularPricePerGram = pricingType === 'MEDICAL' ? oneGramVariant.priceMed : oneGramVariant.priceRec;
    const baseSpecialPricePerGram = pricingType === 'MEDICAL' ? oneGramVariant.specialPriceMed : oneGramVariant.specialPriceRec;
    
    const regularPrice = (baseRegularPricePerGram || 0) * weightInGrams * quantity;
    const specialPrice = baseSpecialPricePerGram ? baseSpecialPricePerGram * weightInGrams * quantity : null;
    
    const hasDiscount = baseRegularPricePerGram !== null && baseSpecialPricePerGram !== null && baseSpecialPricePerGram < baseRegularPricePerGram;
    const discountPercent = hasDiscount && baseRegularPricePerGram ? 
      Math.round(((baseRegularPricePerGram - baseSpecialPricePerGram!) / baseRegularPricePerGram) * 100) : 0;

    return {
      regularPrice,
      specialPrice,
      hasDiscount,
      discountPercent
    };
  };

  const handleAddToCart = async () => {
    if (!product) return;

    // Maryland recreational infused product limit check (12g flower equivalent max)
    const targetVariant = isVermontStore && (isFlowerProduct || (isConcentrateProduct && hasMultipleVariants))
      ? (selectedVariant || getSortedVariants()[0])
      : product.variants?.[0];
    const itemQuantity = isVermontStore && (isFlowerProduct || (isConcentrateProduct && hasMultipleVariants))
      ? 1
      : selectedQuantity;

    if (wouldExceedInfusedLimit(cart, menuProducts, product, targetVariant, itemQuantity)) {
      if (onShowError) {
        onShowError(`Adding this product would exceed the ${INFUSED_LIMIT_GRAMS}g limit for infused products`);
      }
      return;
    }

    try {
      // Remove any existing cart item for this product
      const existingCartItem = findExistingCartItem();
      if (existingCartItem) {
        await removeFromCart(existingCartItem.id);
      }

      // Add new variant based on store type and product category
      if (isVermontStore && (isFlowerProduct || (isConcentrateProduct && hasMultipleVariants))) {
        // Vermont flower or multi-variant concentrates: Use selected variant with quantity 1
        const targetVariant = selectedVariant || getSortedVariants()[0];
        if (targetVariant) {
          await addToCart(product, targetVariant, 1);
        }
      } else {
        // Non-Vermont OR Vermont single-variant products: Use selected quantity directly
        if (selectedQuantity > 0) {
          const targetVariant = product.variants?.[0] || null; // Use first available variant

          await addToCart(product, targetVariant, selectedQuantity);
        }
      }

      // Close the drawer after cart update
      onClose();
    } catch (error) {
      console.error('Error updating product in cart:', error);
    }
  };

  // Handle quantity changes in real-time (update cart immediately)
  const handleQuantityChange = async (newQuantity: number) => {
    if (!product) return;

    setSelectedQuantity(newQuantity);

    try {
      const existingCartItem = findExistingCartItem();

      if (newQuantity === 0) {
        // Remove item from cart when quantity reaches zero
        if (existingCartItem) {
          await removeFromCart(existingCartItem.id);
        }
      } else {
        // Update cart with new quantity
        if (existingCartItem) {
          // Update existing item's quantity
          await updateQuantity(existingCartItem.id, newQuantity);
        } else {
          // Add new item to cart
          const targetVariant = product.variants?.[0] || null;
          await addToCart(product, targetVariant, newQuantity);
        }
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // Early return if product is null or undefined (after all hooks)
  if (!product) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`product-drawer-backdrop ${animate ? 'open' : ''}`}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`product-detail-drawer ${animate ? 'open' : ''}`}>
        <div className="drawer-content">
          {/* Close button */}
          <button className="drawer-close-button" onClick={onClose} aria-label="Close drawer">
            ×
          </button>
          
          <div className="drawer-body">
            {/* Left side - Product image */}
            <div className="drawer-image-section">
              {product.images && product.images[selectedImageIndex] ? (
                <img 
                  src={product.images[selectedImageIndex].url} 
                  alt={product.name}
                  className="drawer-product-image"
                />
              ) : (
                <div className="drawer-image-placeholder">
                  No image available
                </div>
              )}
              
              {/* Thumbnail images - only for Connecticut stores */}
              {product.images && product.images.length > 1 && isConnecticutStore(menuId) && (
                <div className="drawer-thumbnails">
                  {product.images.map((image, index) => (
                    <img
                      key={index}
                      src={image.url}
                      alt={`${product.name} view ${index + 1}`}
                      className={`drawer-thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                      onClick={() => setSelectedImageIndex(index)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Right side - Product details */}
            <div className="drawer-details-section">
              {/* Product header */}
              <div className="drawer-header">
                {product.brand?.name && (
                  <div className="drawer-brand-name">{product.brand.name}</div>
                )}
                <h1 className="drawer-product-name">{formatProductName(product.name)}</h1>
                <div className="drawer-product-info">
                  {product.potencyThc?.formatted && product.potencyThc.formatted !== '0%' && product.potencyThc.formatted !== '0.00%' && (
                    <span className="drawer-thc">THC: {product.potencyThc.formatted}</span>
                  )}
                  {product.strainType && formatStrainType(product.strainType) && (
                    <span className="drawer-strain-type">{formatStrainType(product.strainType)}</span>
                  )}
                  {getProductWeights(product) && (
                    <span className="drawer-weight-info">{getProductWeights(product)}</span>
                  )}
                </div>
              </div>
              
              {/* Product controls */}
              <div className="drawer-controls">
                {showWeightSelector && (
                  <div className="drawer-weight-section">
                    <label className="drawer-control-label">WEIGHT</label>
                    {isVermontStore ? (
                      <div className="drawer-variant-dropdown-container">
                        <div 
                          className="drawer-variant-trigger"
                          onClick={() => setVariantDropdownOpen(!variantDropdownOpen)}
                        >
                          <span className="drawer-variant-selected">
                            {selectedVariant ? selectedVariant.option.toUpperCase() : getSortedVariants()[0]?.option?.toUpperCase() || '1G'}
                          </span>
                          <span className="drawer-variant-arrow">▼</span>
                        </div>
                        {variantDropdownOpen && (
                          <div className="drawer-variant-dropdown">
                            {getSortedVariants().map((variant) => {
                              const price = variant.priceRec || variant.priceMed || 0;
                              const specialPrice = variant.specialPriceRec || variant.specialPriceMed;
                              const displayPrice = (specialPrice && specialPrice < price) 
                                ? `$${specialPrice.toFixed(2)} (was $${price.toFixed(2)})`
                                : `$${price.toFixed(2)}`;
                              
                              return (
                                <div
                                  key={variant.id}
                                  className="drawer-variant-option"
                                  onClick={() => {
                                    setSelectedVariant(variant);
                                    setVariantDropdownOpen(false);
                                  }}
                                >
                                  <span className="drawer-variant-option-name">{variant.option.toUpperCase()}</span>
                                  <span className="drawer-variant-option-price">{displayPrice}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="drawer-weight-controls">
                        <button 
                          className="drawer-quantity-btn"
                          onClick={() => setSelectedWeight(getPreviousWeight(selectedWeight))}
                          disabled={selectedWeight <= 1}
                        >
                          −
                        </button>
                        <span className="drawer-quantity-value">
                          {selectedWeight % 1 === 0 ? `${selectedWeight}g` : `${selectedWeight.toFixed(1)}g`}
                        </span>
                        <button 
                          className="drawer-quantity-btn"
                          onClick={() => setSelectedWeight(getNextWeight(selectedWeight))}
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!showWeightSelector && shouldDisplayWeight(product) && getWeightDisplayText(product, menuId) && (
                  <div className="drawer-weight-display">
                    <label className="drawer-control-label">WEIGHT</label>
                    <div className={`drawer-weight-value ${menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC && product.category?.toLowerCase() === 'flower' && product.variants && product.variants.length > 1 ? 'weight-variants' : ''} ${(() => {
                      const weightText = getWeightDisplayText(product, menuId);
                      // Only apply on desktop (checked via isMobile state)
                      if (!isMobile) {
                        if (weightText.length >= 8) return 'weight-text-small';
                        if (weightText.length >= 6) return 'weight-text-medium';
                      }
                      return '';
                    })()}`}>{getWeightDisplayText(product, menuId)}</div>
                  </div>
                )}
                {showQuantitySelector && (
                  <div className="drawer-quantity-section">
                    <label className="drawer-control-label">QUANTITY</label>
                    <div className="drawer-quantity-controls">
                      <button
                        className="drawer-quantity-btn"
                        onClick={() => handleQuantityChange(Math.max(0, selectedQuantity - 1))}
                        disabled={selectedQuantity <= 0}
                      >
                        −
                      </button>
                      <span className="drawer-quantity-value">{selectedQuantity}</span>
                      <button
                        className="drawer-quantity-btn"
                        onClick={() => handleQuantityChange(selectedQuantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
                <button className="drawer-add-to-cart" onClick={handleAddToCart}>
                  <div className="drawer-add-to-cart-circle"></div>
                  <span className="drawer-add-to-cart-text">
                    {(() => {
                      // Determine button text based on whether item is in cart
                      const isInCart = getCartQuantityForProduct() > 0;
                      const buttonText = isInCart ? 'ADDED' : 'ADD';

                      return buttonText;
                    })()} — {(() => {
                      // Calculate pricing based on store type and product category
                      if (isVermontStore && (isFlowerProduct || (isConcentrateProduct && hasMultipleVariants))) {
                        // Vermont variant-based pricing for flowers and multi-variant concentrates
                        const currentVariant = selectedVariant || getSortedVariants()[0];
                        if (!currentVariant) return '$0.00';

                        // Use correct price field based on pricingType
                        const variantPrice = pricingType === 'MEDICAL'
                          ? (currentVariant.priceMed || currentVariant.priceRec || 0)
                          : (currentVariant.priceRec || currentVariant.priceMed || 0);
                        const specialPrice = pricingType === 'MEDICAL'
                          ? (currentVariant.specialPriceMed || currentVariant.specialPriceRec)
                          : (currentVariant.specialPriceRec || currentVariant.specialPriceMed);
                        
                        if (specialPrice && specialPrice < variantPrice) {
                          return (
                            <>
                              <span className="cart-regular-price">${variantPrice.toFixed(2)}</span>
                              <span className="cart-special-price">${specialPrice.toFixed(2)}</span>
                            </>
                          );
                        } else {
                          return `$${variantPrice.toFixed(2)}`;
                        }
                      } else {
                        // Quantity-based pricing for all other products
                        const qty = selectedQuantity;
                        const weight = 1;
                        const pricing = getProductPricing(product, qty, weight);
                        
                        return pricing.hasDiscount ? (
                          <>
                            <span className="cart-regular-price">${pricing.regularPrice.toFixed(2)}</span>
                            <span className="cart-special-price">${pricing.specialPrice?.toFixed(2)}</span>
                          </>
                        ) : (
                          `$${pricing.regularPrice.toFixed(2)}`
                        );
                      }
                    })()}
                  </span>
                </button>
              </div>
              
              {/* Collapsible sections */}
              <div className="drawer-accordion-sections">
                {/* Description */}
                {(product.descriptionHtml || product.description) && (
                  <AccordionSection 
                    title="DESCRIPTION" 
                    isOpen={false}
                    content={(() => {
                      // Try to use HTML description first, fall back to plain text
                      if (product.descriptionHtml) {
                        try {
                          // Validate that descriptionHtml is a non-empty string
                          if (typeof product.descriptionHtml === 'string' && product.descriptionHtml.trim()) {
                            return (
                              <div 
                                className="accordion-description accordion-description-html"
                                dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                              />
                            );
                          }
                        } catch (error) {
                          console.error('Error rendering HTML description:', error);
                        }
                      }
                      // Fallback to plain text description
                      return <p className="accordion-description">{product.description}</p>;
                    })()}
                  />
                )}
                
                {/* Effects */}
                {getAllEffects(product).length > 0 && (
                  <AccordionSection 
                    title="EFFECTS" 
                    isOpen={false}
                    content={
                      <div className="accordion-list">
                        {getAllEffects(product).map((effect, index) => (
                          <div key={index} className="accordion-item">
                            <span className="accordion-item-name">{effect}</span>
                          </div>
                        ))}
                      </div>
                    }
                  />
                )}
                
                {/* Terpenes */}
                {getAllTerpenes(product).length > 0 && (
                  <AccordionSection 
                    title="TERPENES" 
                    isOpen={false}
                    content={
                      <div className="accordion-list">
                        {getAllTerpenes(product).map((terpene, index) => (
                          <div key={index} className="accordion-item">
                            <span className="accordion-item-name">{terpene.name}</span>
                            <span className="accordion-item-value">{terpene.value?.toFixed(3)}%</span>
                          </div>
                        ))}
                      </div>
                    }
                  />
                )}
                
                {/* Cannabinoids */}
                {getAllCannabinoids(product).length > 0 && (
                  <AccordionSection
                    title="CANNABINOIDS"
                    isOpen={false}
                    content={
                      <div className="accordion-list">
                        {getAllCannabinoids(product).map((cannabinoid, index) => (
                          <div key={index} className="accordion-item">
                            <span className="accordion-item-name">{cannabinoid.name}</span>
                            <span className="accordion-item-value">{formatCannabinoidValue(cannabinoid.value || 0, cannabinoid.unitSymbol)}</span>
                          </div>
                        ))}
                      </div>
                    }
                  />
                )}
              </div>

              {/* Vermont Compliance Disclaimer */}
              {isVermontStoreById(menuId) && (
                <div className="vermont-compliance-disclaimer">
                  <p>
                    Cannabis has not been analyzed or approved by the Food and Drug Administration (FDA). For use by individuals 21 years of age and older or registered qualifying patient only. <strong>KEEP THIS PRODUCT AWAY FROM CHILDREN AND PETS. DO NOT USE IF PREGNANT OR BREASTFEEDING.</strong> Possession or use of cannabis may carry significant legal penalties in some jurisdictions and under federal law. It may not be transported outside of the state of Vermont. <strong>The effects of edible cannabis may be delayed by two hours or more.</strong> Cannabis may be habit forming and can impair concentration, coordination, and judgment. Persons 25 years and younger may be more likely to experience harm to the developing brain.
                  </p>
                  <p>
                    It is against the law to drive or operate machinery when under the influence of this product. National Poison Control Center 1-800-222-1222.
                  </p>
                </div>
              )}

              {/* Connecticut Pharmacist Disclaimer */}
              {isConnecticutStore(menuId) && (
                <div className="connecticut-compliance-disclaimer">
                  <p>Pharmacist available for consultation.</p>
                </div>
              )}
            </div>
          </div>

          {/* You Might Like Section - Full Width Below Main Content */}
          {allProducts.length > 0 && onProductClick && onAddToCart && (
            <YouMightLike
              currentProduct={product}
              allProducts={allProducts}
              menuId={menuId}
              pricingType={pricingType}
              cart={cartProp || cart}
              cartLoading={cartLoading}
              addingToCart={addingToCart}
              activeMarquees={activeMarquees}
              stoppingMarquees={stoppingMarquees}
              dealDescriptions={dealDescriptions}
              productTileCursorStyle={productTileCursorStyle}
              isMobile={isMobileProp !== undefined ? isMobileProp : isMobile}
              selectedTerpenes={selectedTerpenes}
              selectedCannabinoids={selectedCannabinoids}
              onProductClick={onProductClick}
              onProductHover={onProductHover}
              onProductLeave={onProductLeave}
              onAddToCart={onAddToCart}
              onDebugClick={onDebugClick}
              onDealClick={onDealClick}
              onVermontIncrement={onVermontIncrement}
              onVermontDecrement={onVermontDecrement}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ProductDetailDrawer;